// LLM generated - Model Claude 4 Sonnet
import type {
  // Pusher types
  PusherUserAuthPayload,
  PusherUserAuthResponse,
  PusherUserAuthSubscriptionsPayload,
  PusherUserAuthSubscriptionsResponse,

  // Chat types
  GetChatsResponse,
  GetChatsQuery,
  UpdateChatPayload,
  UpdateChatAdministrationResponse,
  ChatUserSettings,
  UpdateChatUserSettingsPayload,
  SendMessagePayload,
  SendMessageQuery,
  SendMessageResponse,
  GetMessagesQuery,
  GetMessagesResponse,
  UnreadMessagesResponse,

  // Message types
  PublishMessagePayload,
  EditMessagePayload,
  FlagMessagePayload,

  // Reaction types
  GetReactionsResponse,
  GetMessageReactionsResponse,
  GetMessageReactionsQuery,
  AddReactionPayload,

  // Last read message types
  StoreLastReadMessagePayload,

  // Health types
  HealthResponse,
} from './types';

import { liveActivityService } from './live-activity-service.service.instance';

// =============================================================================
// PUSHER ENDPOINTS
// =============================================================================

/**
 * Authenticate user for Pusher private channels
 * Used for basic private channel authentication
 */
export const authenticateUser = async (
  payload: PusherUserAuthPayload
): Promise<PusherUserAuthResponse> => {
  const response = await liveActivityService.post<PusherUserAuthResponse>(
    '/pusher/user-auth',
    payload
  );
  return response.data;
};

/**
 * Authenticate user for Pusher presence channel subscriptions
 * Used for presence channels where user information is shared
 */
export const authenticateUserSubscriptions = async (
  payload: PusherUserAuthSubscriptionsPayload
): Promise<PusherUserAuthSubscriptionsResponse> => {
  const response = await liveActivityService.post<PusherUserAuthSubscriptionsResponse>(
    '/pusher/user-auth/subscriptions',
    payload
  );
  return response.data;
};

// =============================================================================
// CHAT ENDPOINTS
// =============================================================================

/**
 * Get chats for current user
 * @param query - Optional query parameters including contestId, page, and pageSize
 */
export const getChats = async (query?: GetChatsQuery): Promise<GetChatsResponse> => {
  const response = await liveActivityService.get<GetChatsResponse>('/chats', {
    params: query,
  });
  return response.data;
};

/**
 * Update chat settings
 * @param chatId - The ID of the chat to update
 * @param payload - Chat update payload containing isEnabled flag
 */
export const updateChat = async (chatId: string, payload: UpdateChatPayload) => {
  const response = await liveActivityService.patch(`/chats/${chatId}`, payload);
  return response.data;
};

/**
 * Update user settings for a specific chat
 * @param chatId - The ID of the chat
 * @param payload - User settings payload containing muteNotifications flag
 */
export const updateChatUserSettings = async (
  chatId: string,
  payload: UpdateChatUserSettingsPayload
): Promise<ChatUserSettings> => {
  const response = await liveActivityService.put<ChatUserSettings>(
    `/chats/${chatId}/user-settings`,
    payload
  );
  return response.data;
};

// =============================================================================
// ADMINISTRATION ENDPOINTS
// =============================================================================

/**
 * Update chat administration settings (enable/disable chat)
 * @param chatId - The ID of the chat to update
 * @param payload - Administration payload containing isEnabled flag
 */
export const updateChatAdministration = async (
  chatId: string,
  payload: { isEnabled: boolean }
): Promise<UpdateChatAdministrationResponse> => {
  const response = await liveActivityService.patch<UpdateChatAdministrationResponse>(
    `/administration/chats/${chatId}`,
    payload
  );
  return response.data;
};

export const getMessages = async (
  chatId: string,
  query?: GetMessagesQuery
): Promise<GetMessagesResponse> => {
  const response = await liveActivityService.get<GetMessagesResponse>(`/chats/${chatId}/messages`, {
    params: {
      pageSize: 20, // Default page size
      reversePageOrder: false, // Default value, included for future use
      ...query,
    },
  });
  return response.data;
};

export const getUnreadMessages = async (chatId: string): Promise<UnreadMessagesResponse> => {
  const response = await liveActivityService.get<UnreadMessagesResponse>(
    `/chats/${chatId}/unread-messages`
  );
  return response.data;
};

// =============================================================================
// MESSAGE ENDPOINTS
// =============================================================================

export const sendMessage = async (
  chatId: string,
  payload: SendMessagePayload,
  query?: SendMessageQuery
): Promise<SendMessageResponse> => {
  const response = await liveActivityService.post<SendMessageResponse>(
    `/chats/${chatId}/messages`,
    payload,
    {
      params: query,
    }
  );
  return response.data;
};

export const deleteMessage = async (chatId: string, messageId: string) => {
  const response = await liveActivityService.delete(`/chats/${chatId}/messages/${messageId}`);
  return response.data;
};

export const editMessage = async (
  chatId: string,
  messageId: string,
  payload: EditMessagePayload
) => {
  const response = await liveActivityService.patch(
    `/chats/${chatId}/messages/${messageId}`,
    payload
  );
  return response.data;
};

export const flagMessage = async (
  chatId: string,
  messageId: string,
  payload: FlagMessagePayload
) => {
  const response = await liveActivityService.post(
    `/chats/${chatId}/messages/${messageId}/flags`,
    payload
  );
  return response.data;
};

export const pinMessage = async (chatId: string, messageId: string) => {
  const response = await liveActivityService.post(`/chats/${chatId}/messages/${messageId}/pins`);
  return response.data;
};

export const unpinMessage = async (chatId: string, messageId: string) => {
  const response = await liveActivityService.delete(`/chats/${chatId}/messages/${messageId}/pins`);
  return response.data;
};

// =============================================================================
// REACTION ENDPOINTS
// =============================================================================

export const getAvailableReactions = async (): Promise<GetReactionsResponse> => {
  const response = await liveActivityService.get<GetReactionsResponse>('/chats/reactions');
  return response.data;
};

export const getMessageReactions = async (
  chatId: string,
  messageId: string,
  query?: GetMessageReactionsQuery
): Promise<GetMessageReactionsResponse> => {
  const params = new URLSearchParams();

  if (query?.limit) {
    params.append('limit', query.limit.toString());
  }

  if (query?.cursor) {
    params.append('cursor', query.cursor);
  }

  const queryString = params.toString();
  const url = `/chats/${chatId}/messages/${messageId}/reactions${
    queryString ? `?${queryString}` : ''
  }`;

  const response = await liveActivityService.get<GetMessageReactionsResponse>(url);
  return response.data;
};

export const addReaction = async (
  chatId: string,
  messageId: string,
  payload: AddReactionPayload
) => {
  const response = await liveActivityService.post(
    `/chats/${chatId}/messages/${messageId}/reactions`,
    payload
  );
  return response.data;
};

export const removeReaction = async (chatId: string, messageId: string, reactionCode: string) => {
  const response = await liveActivityService.delete(
    `/chats/${chatId}/messages/${messageId}/reactions/${reactionCode}`
  );
  return response.data;
};

// =============================================================================
// LAST READ MESSAGE ENDPOINTS
// =============================================================================

export const storeLastReadMessage = async (payload: StoreLastReadMessagePayload) => {
  const response = await liveActivityService.post('/chats/last-read-messages', payload);
  return response.data;
};

// =============================================================================
// HEALTH ENDPOINTS
// =============================================================================

export const getHealth = async (): Promise<HealthResponse> => {
  const response = await liveActivityService.get<HealthResponse>('/health');
  return response.data;
};

export const getSimpleHealth = async (): Promise<HealthResponse> => {
  const response = await liveActivityService.get<HealthResponse>('/health');
  return response.data;
};
